// pushNotifications.js - Subscrição de notificações no browser
//
// O browser subscreve-se ao serviço de push do próprio fabricante e
// devolve um endpoint; guardamos esse endpoint no servidor. Daí em diante
// é o serviço do fabricante que entrega as mensagens ao dispositivo - o
// nosso servidor nunca fala diretamente com o telemóvel.
//
// IMPORTANTE NO iPHONE E iPAD: as notificações web só funcionam com a app
// instalada no ecrã principal (Partilhar -> Adicionar ao Ecrã Principal).
// Num separador do Safari o browser nem sequer deixa subscrever. É
// limitação da Apple, não nossa - daí o `podeSubscrever` verificar isso e
// a UI explicá-lo em vez de mostrar um botão que não funcionaria.

import i18n from './i18n';

const API_URL = '/api';

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// A chave VAPID viaja em base64url; a API do browser quer bytes.
function base64UrlParaUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normal);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function estaInstaladaNoEcraPrincipal() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

export function ehIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** O browser suporta notificações e está num contexto onde as permite? */
export function podeSubscrever() {
  const suportado = 'serviceWorker' in navigator &&
                    'PushManager' in window &&
                    'Notification' in window;
  if (!suportado) return false;
  // No iOS só dentro da app instalada
  if (ehIOS() && !estaInstaladaNoEcraPrincipal()) return false;
  return true;
}

/** Motivo legível para quando não dá - para a UI explicar em vez de esconder */
export function motivoIndisponivel() {
  if (ehIOS() && !estaInstaladaNoEcraPrincipal()) {
    return i18n.t('notificacoes.iosPrecisaInstalar');
  }
  if (!('Notification' in window)) {
    return i18n.t('notificacoes.browserNaoSuporta');
  }
  return i18n.t('notificacoes.contextoNaoPermite');
}

export async function estadoDaSubscricao() {
  if (!podeSubscrever()) return { subscrito: false, permissao: 'unsupported' };

  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();
  return { subscrito: !!sub, permissao: Notification.permission };
}

/** Pedir permissão e registar este dispositivo. Devolve {ok, erro}. */
export async function subscrever() {
  if (!podeSubscrever()) {
    return { ok: false, erro: motivoIndisponivel() };
  }

  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') {
    return {
      ok: false,
      erro: permissao === 'denied'
        ? i18n.t('notificacoes.permissaoBloqueada')
        : i18n.t('notificacoes.permissaoNegada'),
    };
  }

  try {
    const resposta = await fetch(`${API_URL}/push/key`, { headers: authHeaders() });
    const dados = await resposta.json();
    if (!dados.enabled || !dados.public_key) {
      return { ok: false, erro: i18n.t('notificacoes.servidorSemConfig') };
    }

    const registration = await navigator.serviceWorker.ready;

    // Se já existir subscrição, reutiliza-se
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlParaUint8Array(dados.public_key),
      });
    }

    const guardar = await fetch(`${API_URL}/push/subscribe`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(sub.toJSON()),
    });

    if (!guardar.ok) {
      const e = await guardar.json().catch(() => ({}));
      return { ok: false, erro: e.detail || i18n.t('notificacoes.servidorRecusou') };
    }

    return { ok: true };
  } catch (err) {
    return { ok: false, erro: `Não foi possível subscrever: ${err.message}` };
  }
}

/** Deixar de receber notificações neste dispositivo */
export async function cancelarSubscricao() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return { ok: true };

    await fetch(`${API_URL}/push/unsubscribe`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
    await sub.unsubscribe();
    return { ok: true };
  } catch (err) {
    return { ok: false, erro: err.message };
  }
}

/** Notificação de teste, para confirmar que o caminho todo funciona */
export async function enviarTeste() {
  const r = await fetch(`${API_URL}/push/test`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const dados = await r.json().catch(() => ({}));
  return r.ok ? { ok: true, enviadas: dados.sent } : { ok: false, erro: dados.detail };
}
